import { create } from "zustand";

export interface FlyingItem {
  id: string;
  image?: string;
  from: { x: number; y: number; size: number };
}

interface CartUiState {
  isOpen: boolean;
  cartAnchor: HTMLElement | null;
  flyingItems: FlyingItem[];

  open: () => void;
  close: () => void;
  toggle: () => void;
  setCartAnchor: (el: HTMLElement | null) => void;

  /**
   * Play the fly-to-cart animation from `source` and open the cart popover
   * once the item "lands". `source` can be an HTMLElement or a DOMRect-like.
   */
  playFlyToCart: (args: {
    source: HTMLElement | DOMRect | null;
    image?: string;
  }) => void;

  _removeFlyingItem: (id: string) => void;
}

export const FLY_ANIMATION_MS = 650;

export const useCartUiStore = create<CartUiState>((set, get) => ({
  isOpen: false,
  cartAnchor: null,
  flyingItems: [],

  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  toggle: () => set((s) => ({ isOpen: !s.isOpen })),
  setCartAnchor: (el) => set({ cartAnchor: el }),

  playFlyToCart: ({ source, image }) => {
    const rect =
      source instanceof HTMLElement
        ? source.getBoundingClientRect()
        : (source as DOMRect | null);
    if (!rect) {
      // No source — just open the cart.
      set({ isOpen: true });
      return;
    }

    const size = Math.min(rect.width, rect.height, 96);
    const item: FlyingItem = {
      id: `fly-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      image,
      from: {
        x: rect.left + rect.width / 2 - size / 2,
        y: rect.top + rect.height / 2 - size / 2,
        size,
      },
    };
    set((s) => ({ flyingItems: [...s.flyingItems, item] }));

    // Open the cart a touch before the image lands so the drop feels connected.
    window.setTimeout(() => set({ isOpen: true }), FLY_ANIMATION_MS - 150);
    window.setTimeout(() => get()._removeFlyingItem(item.id), FLY_ANIMATION_MS + 50);
  },

  _removeFlyingItem: (id) =>
    set((s) => ({ flyingItems: s.flyingItems.filter((i) => i.id !== id) })),
}));
